import {
  Box,
  FormHelperText,
  FormHelperTextProps,
  InputLabel,
  InputLabelProps,
  Typography,
} from "@mui/material";
import { CSSObject } from "@mui/material/styles";
import { DatePicker, DateTimeValidationError } from "@mui/x-date-pickers";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import {
  Control,
  FieldValues,
  Path,
  useController,
  useFormContext,
} from "react-hook-form";

import theme, { palette } from "~frontend/theme";

export function RhfDatePicker<T extends FieldValues>({
  name,
  label,
  minDate,
  minDateError,
  maxDate,
  maxDateError,
  timezone,
  fullWidth,
  helperText,
  format, // Format reference here: https://moment.github.io/luxon/#/formatting?id=table-of-tokens
  onError,
  disabled,
  customError,
  inputLabelProps,
  helperTextProps,
  control,
  isSlim = false,
  backgroundColor = palette.white,
  optional,
  onChange: onChangeProp,
  sx = {},
}: {
  name: Path<T>;
  label: string;
  minDate?: DateTime;
  minDateError?: string;
  maxDate?: DateTime;
  maxDateError?: string;
  timezone?: string;
  fullWidth?: boolean;
  helperText?: string;
  format?: string;
  onError?: any;
  disabled?: boolean;
  customError?: { errorText: string };
  inputLabelProps?: InputLabelProps;
  helperTextProps?: FormHelperTextProps;
  control: Control<T>;
  isSlim?: boolean;
  backgroundColor?: string;
  optional?: boolean;
  onChange?: (value: DateTime | null) => void;
  sx?: CSSObject;
}): ReactElement {
  const { clearErrors, formState } = useFormContext();
  const { field, fieldState } = useController({
    control,
    name,
  });

  const maxMinErrorString = (error: DateTimeValidationError) => {
    switch (error) {
      case "minDate":
        return minDateError;
      case "maxDate":
        return maxDateError;
      default:
        return "";
    }
  };

  const error = customError ? customError.errorText : fieldState.error?.message;
  const hasError = (fieldState.isTouched || formState.isSubmitted) && !!error;

  return (
    <Box sx={{ width: fullWidth ? "100%" : undefined }}>
      {label ? (
        <InputLabel shrink {...inputLabelProps}>
          {label}
          {optional ? (
            <Typography component="span"> (optional)</Typography>
          ) : null}
        </InputLabel>
      ) : null}
      <DatePicker
        value={
          typeof field.value === "string"
            ? DateTime.fromISO(field.value)
            : field.value
        }
        onChange={(value: DateTime | null) => {
          field.onChange(value);
          onChangeProp?.(value);
        }}
        timezone={timezone}
        onError={
          onError
            ? onError
            : async newError => {
                if (newError) {
                  control.setError(field.name, {
                    message: maxMinErrorString(newError),
                  });
                } else {
                  clearErrors(field.name);
                }
              }
        }
        minDate={minDate}
        maxDate={maxDate}
        sx={{
          borderRadius: 0,
          paddingTop: label ? 0 : undefined,
          width: fullWidth ? "100%" : undefined,
          marginBottom: 0,
          "& .MuiInputAdornment-root": {
            width: "48px",
            justifyContent: "flex-end",
          },
          "& .MuiPickersInputBase-root": {
            borderRadius: 0,
            backgroundColor,
            fontSize: isSlim ? "11px" : undefined,
            width: isSlim ? "120px" : undefined,
            ...(hasError && {
              "& fieldset": {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
              },
              "&:hover fieldset": {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
              },
              "&.Mui-focused fieldset": {
                borderColor: theme.palette.error.main,
                borderWidth: 2,
              },
            }),
            ...sx,
          },
        }}
        format={format}
        disabled={disabled}
        slotProps={{
          textField: {
            variant: isSlim ? "standard" : undefined,
          },
        }}
      />
      {(hasError && error) || helperText ? (
        <FormHelperText
          sx={{
            paddingLeft: theme.spacing(2),
            paddingTop: 0,
            marginTop: 0,
            fontSize: "10px",
          }}
          error={hasError}
          {...helperTextProps}
        >
          {(hasError && error) || helperText}
        </FormHelperText>
      ) : null}
    </Box>
  );
}
